// PlayFirst Parent — Contact & Support page.
// /parent-app/contact
//
// Warm, parent-friendly support hub: how to reach the team, common FAQs,
// and quick links to Privacy + Terms.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, MessageCircle, ChevronDown, ShieldCheck, FileText, Heart } from 'lucide-react';
import { PFP } from './styles';

const SUPPORT_EMAIL = 'michaela@playfirstapp.com';

const FAQS = [
  {
    q: 'How do I activate my subscription after paying on Paystack?',
    a: 'Once your Paystack payment goes through, please forward the receipt to michaela@playfirstapp.com with your account email. Your account will be activated within 24 hours (usually a lot sooner!). We are still setting up automatic activation — sorry for the small wait.',
  },
  {
    q: "I can't log in / I forgot my password.",
    a: 'On the login page, tap "Forgot password" and follow the reset link sent to your email. If the email never arrives, check spam first, then email michaela@playfirstapp.com so we can reset it manually.',
  },
  {
    q: 'Can I add more than one child?',
    a: "Yes! On the Home page, tap your child's name in the header to switch between children, or tap the small + button to add another little one. You only need one paid subscription per family.",
  },
  {
    q: 'How do I connect WhatsApp?',
    a: 'Open Settings, scroll to "WhatsApp Companion", and follow the connect link. We send daily play ideas, a weekly summary every Monday, and a gentle monthly stage note — all of which you can pause or change anytime.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. We never sell your data and only the minimum information needed to run the app is stored. Read the full Privacy Policy below for the details.',
  },
  {
    q: 'I want to cancel my subscription.',
    a: 'Email michaela@playfirstapp.com from your account email and we will cancel right away. There is no contract — you can come back anytime.',
  },
  {
    q: "My child's behaviour worries me. What should I do?",
    a: "PlayFirst Parent is not a diagnostic or emergency tool. If you are worried, please speak with a qualified health professional. Browse 'Our Collaborators' in the app for early-intervention friendly providers we have curated.",
  },
];

export default function ParentContactSupport() {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className={PFP.page + ' min-h-screen'} data-testid="parent-contact-support-page">
      <header className="max-w-3xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
          data-testid="contact-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Link to="/parent-app/home" className="text-sm text-[#7C6F9C] hover:underline">
          PlayFirst Parent
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 pb-16 space-y-6">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-[#FBF4F9] text-[#7C6F9C] flex items-center justify-center">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-serif text-3xl text-[#3a2f4f]">Contact &amp; Support</h1>
              <p className="text-sm text-stone-500 mt-1">
                We&apos;re a small team. We read every message — and we usually reply within a day.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Email card */}
        <section className={`${PFP.card} p-6`} data-testid="contact-email-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-xl text-[#3a2f4f]">Email us</h2>
              <p className="text-sm text-stone-600 mt-1">
                For anything — questions, activation, payment receipts, feedback or a quick hello.
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=PlayFirst%20Parent%20support`}
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-[#7C6F9C] text-white text-sm font-medium hover:bg-[#6a5e8a] transition"
                data-testid="contact-email-link"
              >
                <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </section>

        {/* WhatsApp card */}
        <section className={`${PFP.card} p-6`} data-testid="contact-whatsapp-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-green-50 text-green-700 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-xl text-[#3a2f4f]">WhatsApp the founder</h2>
              <p className="text-sm text-stone-600 mt-1">
                For something urgent, message Michaela directly on WhatsApp. Replies are best effort,
                weekdays during South African office hours.
              </p>
              <a
                href="https://wa.me/27795157938?text=Hi%20Michaela%2C%20I%E2%80%99m%20a%20PlayFirst%20Parent%20user."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition"
                data-testid="contact-whatsapp-link"
              >
                <MessageCircle className="w-4 h-4" /> Open WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className={`${PFP.card} p-6`} data-testid="contact-faqs">
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-4">Common questions</h2>
          <div className="divide-y divide-stone-200/80">
            {FAQS.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <div key={idx} className="py-3">
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? null : idx)}
                    className="w-full flex items-center justify-between gap-3 text-left"
                    data-testid={`faq-toggle-${idx}`}
                  >
                    <span className="text-sm sm:text-base font-medium text-[#3a2f4f]">{item.q}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-stone-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {open && (
                    <p className="mt-2 text-sm text-stone-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>
                      {item.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Legal links */}
        <section className={`${PFP.card} p-6`} data-testid="contact-legal">
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-3">Privacy &amp; Terms</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to="/privacy"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 text-stone-700 hover:bg-stone-50 text-sm"
              data-testid="contact-privacy-link"
            >
              <ShieldCheck className="w-4 h-4" /> Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 text-stone-700 hover:bg-stone-50 text-sm"
              data-testid="contact-terms-link"
            >
              <FileText className="w-4 h-4" /> Terms of Service
            </Link>
          </div>
        </section>

        <p className="text-center text-xs text-stone-400 pt-2">
          PlayFirst Parent is not a diagnostic, screening or emergency tool. If you are concerned about
          your child&apos;s communication or development, please speak with a qualified health professional.
        </p>
      </main>
    </div>
  );
}
